import React, { useEffect, useState, useRef } from "react"
import { useDispatch, useSelector } from "react-redux"
import { fetchBoardByIdAction } from "../../../utils/redux/projectBoard/projectBoardActions"
import { FiDownload } from "react-icons/fi"
import styles from "./ProjectBoard.module.css"
import { IoMdShare } from "react-icons/io"
import { FaRegCopy, FaRegTrashCan } from "react-icons/fa6"
import RoomItem from "./RoomItem/RoomItem"
import { toast } from "react-toastify"
import Cookies from "js-cookie"
import axios from "axios"
import { useParams } from "react-router-dom"

const ProjectBoardPage = () => {
    const { id } = useParams();
    const dispatch = useDispatch()
    const boardId = id

    const { selectedBoard: board, isLoading } = useSelector(
        state => state.projectBoard
    )
    const [boardName, setBoardName] = useState("")
    const [showDownloadDropdown, setShowDownloadDropdown] = useState(false)
    const dropdownRef = useRef(null)

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = event => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowDownloadDropdown(false)
            }
        }
        document.addEventListener("mousedown", handleClickOutside)
        return () => document.removeEventListener("mousedown", handleClickOutside)
    }, [])

    // Fetch board data on mount
    useEffect(() => {
        if (boardId) {
            dispatch(fetchBoardByIdAction(boardId))
        }
    }, [boardId, dispatch])

    // Update local board name when board data loads
    useEffect(() => {
        if (board?.name) {
            setBoardName(board.name)
        }
    }, [board?.name])

    const handleDownloadPDF = async () => {
        setShowDownloadDropdown(false)
        try {
            const token = Cookies.get("jwt")
            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/project-board/download/pdf/${boardId}`,
                {
                    method: "GET",
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                }
            )

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`)
            }

            const blob = await response.blob()
            const contentType = response.headers.get("content-type")

            if (!contentType || !contentType.includes("application/pdf")) {
                const text = await blob.text()
                const newWindow = window.open()
                if (newWindow) {
                    newWindow.document.write(text)
                    newWindow.document.close()
                }
                toast.success(
                    "Document opened in new window. Use Ctrl+P to save as PDF."
                )
                return
            }

            const url = window.URL.createObjectURL(blob)
            const a = document.createElement("a")
            a.href = url
            a.download = `${board?.project_id?.project_code || ''}_${board?.project_id?.name || ''}_presentation.pdf`
            document.body.appendChild(a)
            a.click()
            document.body.removeChild(a)
            window.URL.revokeObjectURL(url)
            toast.success("PDF downloaded successfully")
        } catch (error) {
            console.error("Error downloading PDF:", error)
            toast.error(error?.message || "Failed to download PDF")
        }
    }

    const handleDownloadExcel = async () => {
        setShowDownloadDropdown(false)
        try {
            const token = Cookies.get("jwt")
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/project-board/download/excel/${boardId}`,
                {
                    responseType: "blob",
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                }
            )

            if (response.status === 200) {
                const blob = response.data
                const url = window.URL.createObjectURL(blob)
                const a = document.createElement("a")
                a.href = url
                a.download = `${board?.project_id?.project_code || ''}_${board?.project_id?.name || ''}_summary_quotation.xlsx`
                document.body.appendChild(a)
                a.click()
                window.URL.revokeObjectURL(url)
                document.body.removeChild(a)
                toast.success("Excel downloaded successfully")
            } else {
                toast.error("Failed to download Excel")
            }
        } catch (error) {
            console.error("Error downloading Excel:", error)
            const errorMessage =
                error?.response?.data?.message || "Failed to download Excel"
            toast.error(errorMessage)
        }
    }

    const handleDownloadBifurcatedExcel = async () => {
        setShowDownloadDropdown(false);
        try {
            const token = Cookies.get('jwt');
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/project-board/download/excel/bifurcated/${boardId}`, {
                responseType: 'blob',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.status === 200) {
                const blob = response.data;
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `${board?.project_id?.project_code || ''}_${board?.project_id?.name || ''}_summary_quotation.xlsx`;
                document.body.appendChild(a);
                a.click();
                window.URL.revokeObjectURL(url);
                document.body.removeChild(a);
                toast.success('Excel downloaded successfully');
            } else {
                toast.error('Failed to download Excel');
            }
        } catch (error) {
            console.error('Error downloading Excel:', error);
            const errorMessage = error?.response?.data?.message || 'Failed to download Excel';
            toast.error(errorMessage);
        }
    }

    const handleDownloadDetailedExcel = async (pricingType = "cost") => {
        setShowDownloadDropdown(false);
        try {
            const token = Cookies.get('jwt');
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/project-board/download/excel/detailed/${boardId}?pricingType=${pricingType}`, {
                responseType: 'blob',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.status === 200) {
                const blob = response.data;
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `${board?.project_id?.project_code || ''}_${board?.project_id?.name || ''}_detailed_quotation_${pricingType === "cost" ? "CP" : pricingType === "selling" ? "SP" : ""}.xlsx`;
                document.body.appendChild(a);
                a.click();
                window.URL.revokeObjectURL(url);
                document.body.removeChild(a);
                toast.success('Excel downloaded successfully');
            } else {
                toast.error('Failed to download Excel');
            }
        } catch (error) {
            console.error('Error downloading Excel:', error);
            const errorMessage = error?.response?.data?.message || 'Failed to download Excel';
            toast.error(errorMessage);
        }
    }

    const handleDownloadClick = () => {
        setShowDownloadDropdown(!showDownloadDropdown)
    }


    // Get client and architect details from board data
    const clientDetails = board?.project_id?.clientDetails
    const architectDetails = board?.project_id?.architectDetails
    const projectAddress = board?.project_id?.address

    if (isLoading && !board) {
        return (
            <div className={styles.loadingContainer}>
                <div className={styles.spinner}></div>
                <p>Loading board...</p>
            </div>
        )
    }

    if (!board) {
        return (
            <div className={styles.errorContainer}>
                <p>Board not found</p>
            </div>
        )
    }

    return (
        <>
            <div className={styles.container}>
                {/* Header Bar */}
                <div className={styles.header}>
                    <div className={styles.headerLeft}>

                        <div className={styles.boardTitleSection}>
                            <h1 className={styles.boardName}>{board.name}</h1>
                        </div>
                    </div>
                    <div className={styles.headerRight}>

                        {/* Download Button with Dropdown for TEAM/AID */}
                        <div className={styles.downloadContainer} ref={dropdownRef}>
                            <span
                                onClick={handleDownloadClick}
                                className={styles.iconButton}
                                title="Download"
                            >
                                <FiDownload size={20} />
                            </span>
                            {showDownloadDropdown && (
                                <div className={styles.downloadDropdown}>
                                    <button
                                        onClick={handleDownloadPDF}
                                        className={styles.dropdownItem}
                                    >
                                        <FiDownload size={14} /> Download Presentation
                                    </button>
                                    <button
                                        onClick={handleDownloadExcel}
                                        className={styles.dropdownItem}
                                    >
                                        <FiDownload size={14} /> Download Quotation Summary
                                    </button>
                                    <button
                                        onClick={handleDownloadBifurcatedExcel}
                                        className={styles.dropdownItem}
                                    >
                                        <FiDownload size={14} /> Download Bifurcated Quotation Summary
                                    </button>
                                    <button
                                        onClick={() => handleDownloadDetailedExcel("cost")}
                                        className={styles.dropdownItem}
                                    >
                                        <FiDownload size={14} /> Download Detailed Quotation CP
                                    </button>
                                    <button
                                        onClick={() => handleDownloadDetailedExcel("selling")}
                                        className={styles.dropdownItem}
                                    >
                                        <FiDownload size={14} /> Download Detailed Quotation SP
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Project, Client, Architect, Address and SPOC Details - Only visible in VIEW mode */}
                <div
                    className={`d-flex px-5 py-3`}
                    style={{
                        gap: "40px"
                    }}
                >
                    {/* Left Column - Project, Client, Architect, Address */}
                    <div style={{ flex: 1 }}>
                        <div>
                            <strong>Project Name:</strong>{" "}
                            {board?.project_id?.name || "N/A"}
                        </div>
                        <div className="mt-2">
                            <strong>Project Code:</strong>{" "}
                            {board?.project_id?.project_code || "N/A"}
                        </div>
                        {clientDetails && (
                            <>
                                <div className="mt-2">
                                    <strong>Client Name:</strong> {clientDetails.name || "N/A"}
                                </div>
                                <div className="mt-2">
                                    <strong>Client Number:</strong>{" "}
                                    {clientDetails.phone || "N/A"}
                                </div>
                            </>
                        )}
                        {architectDetails && (
                            <div className="mt-2">
                                <strong>Architect Name:</strong>{" "}
                                {architectDetails.name || "N/A"}
                            </div>
                        )}
                        {board?.project_id?.address &&
                            Object.values(board.project_id.address).some(val => val) && (
                                <div className="mt-2">
                                    <strong>Address:</strong>{" "}
                                    {[
                                        board.project_id.address.address_line_1,
                                        board.project_id.address.address_line_2,
                                        board.project_id.address.landmark,
                                        board.project_id.address.city,
                                        board.project_id.address.state,
                                        board.project_id.address.country,
                                        board.project_id.address.pincode
                                    ]
                                        .filter(Boolean)
                                        .join(", ")}
                                </div>
                            )}
                    </div>

                    {/* Right Column - SPOC Details */}
                    <div
                        style={{
                            flex: 1,
                            borderLeft: "1px solid #ddd",
                            paddingLeft: "40px"
                        }}
                    >
                        {board?.project_id?.spoc_details?.spoc_name ? (
                            <>
                                <div>
                                    <strong>SPOC Name:</strong>{" "}
                                    {board.project_id.spoc_details.spoc_name}
                                </div>
                                <div className="mt-2">
                                    <strong>SPOC Phone:</strong>{" "}
                                    {board.project_id.spoc_details.spoc_phone || "N/A"}
                                </div>
                                <div className="mt-2">
                                    <strong>SPOC Email:</strong>{" "}
                                    {board.project_id.spoc_details.spoc_email || "N/A"}
                                </div>
                            </>
                        ) : (
                            <div>
                                <strong>SPOC Details:</strong> Not available
                            </div>
                        )}
                    </div>
                </div>

                {/* Rooms Section */}
                <div className={styles.roomsSection}>
                    {/* Rooms List */}
                    <div className={styles.roomsList}>
                        {board.rooms &&
                            board.rooms.length > 0 &&
                            board.rooms.map((room, index) => (
                                <RoomItem
                                    key={index}
                                    room={room}
                                />
                            ))}
                    </div>
                </div>
            </div>

            {/* Footer Section */}
            <div className={`w-100 d-flex flex-column`}>
                {/* Created By Info */}
                <div
                    className={`d-flex justify-content-end p-4`}
                    style={{
                        border: "1px solid #ddd"
                    }}
                >
                    <div>
                        Created By - {board.owner?.first_name} {board.owner?.last_name};
                        Created on - {new Date(board.createdAt).toLocaleDateString()}
                    </div>
                </div>

                {/* Action Buttons */}
                <div
                    className={`d-flex justify-content-between p-4`}
                    style={{
                        border: "1px solid #ddd"
                    }}
                >
                    <div>Board's Total Value: {board?.boardDisplayPrice?.formatted}</div>
                </div>
            </div>
        </>
    )
}

export default ProjectBoardPage
